import * as i0 from '@angular/core';
import { Pipe, Directive, Input, NgModule } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as i1 from '@angular/platform-browser';
import DOMPurify from 'dompurify';

class KeysPipeObject {
    constructor(key, value) {
        this.key = key;
        this.value = value;
    }
}
class KeysPipe {
    transform(value, args) {
        return Object.keys(value).map(key => {
            return new KeysPipeObject(key, value[key]);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KeysPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: KeysPipe, name: "keys" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KeysPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'keys' }]
        }] });

const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
class BytesPipe {
    transform(bytes, precision) {
        if (typeof precision === 'undefined') {
            precision = 1;
        }
        const num = Math.floor(Math.log(bytes) / Math.log(1024)), q = Math.pow(1024, Math.floor(num));
        return [(bytes / q).toFixed(precision), units[num]].join(' ');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: BytesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: BytesPipe, name: "bytes" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: BytesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'bytes'
                }]
        }] });

class CapitalizePipe {
    transform(value) {
        return (value || '').charAt(0).toUpperCase() + (value || '').slice(1);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: CapitalizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: CapitalizePipe, name: "capitalize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: CapitalizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'capitalize'
                }]
        }] });

class ReplacePipe {
    transform(input, str, val) {
        if (!input) {
            return input;
        }
        return input.replace(str, val);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReplacePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: ReplacePipe, name: "replace" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReplacePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'replace'
                }]
        }] });

class ReversePipe {
    transform(value) {
        if (!value) {
            return null;
        }
        if (value instanceof Observable) {
            return value.pipe(map(v => v.reverse()));
        }
        return value.slice().reverse();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReversePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: ReversePipe, name: "reverse", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReversePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'reverse',
                    pure: false
                }]
        }] });

class SanitizedUrlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(url) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: SanitizedUrlPipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: SanitizedUrlPipe, name: "sanitizedUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: SanitizedUrlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitizedUrl'
                }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }] });

const is_str = (...args) => {
    return args.every(a => typeof a === 'string');
};
class SortPipe {
    transform(array, field) {
        if (!Array.isArray(array)) {
            return [];
        }
        array.sort((a, b) => {
            if (is_str(a, b)) {
                a = a.toLowerCase();
                b = b.toLowerCase();
            }
            else if (field) {
                if (is_str(a[field], b[field])) {
                    a = a[field].toLowerCase();
                    b = b[field].toLowerCase();
                }
                else {
                    a = a[field];
                    b = b[field];
                }
            }
            if (a < b) {
                return -1;
            }
            else if (a > b) {
                return 1;
            }
            else {
                return 0;
            }
        });
        return array;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: SortPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: SortPipe, name: "sort" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: SortPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sort'
                }]
        }] });

class TrimPipe {
    transform(value) {
        if (!value)
            return '';
        return value.trim();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: TrimPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: TrimPipe, name: "trim" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: TrimPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'trim'
                }]
        }] });

class TruncateURLPipe {
    transform(str, len = 50, separator = '...') {
        if (str.length <= len) {
            return str;
        }
        const charsToShow = len - separator.length, frontChars = Math.ceil(charsToShow / 2), backChars = Math.floor(charsToShow / 2);
        return str.substr(0, frontChars) + separator +
            str.substr(str.length - backChars);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: TruncateURLPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: TruncateURLPipe, name: "truncateURL" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: TruncateURLPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'truncateURL'
                }]
        }] });

class HighlightPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(text, search) {
        if (search && text) {
            let pattern = search.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
            pattern = pattern.split(' ').filter(t => t.length > 0).join('|');
            const regex = new RegExp(pattern, 'gi');
            text = text.replace(regex, (match) => `<mark>${match}</mark>`);
        }
        const options = { ALLOWED_TAGS: ['mark'], ALLOWED_ATTR: [] };
        const sanitizedContent = DOMPurify.sanitize(text, options);
        return this.sanitizer.bypassSecurityTrustHtml(sanitizedContent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: HighlightPipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: HighlightPipe, name: "highlight" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: HighlightPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'highlight'
                }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }] });

class SanitizedHtmlPipe {
    constructor(sanitized) {
        this.sanitized = sanitized;
    }
    transform(value) {
        const options = { ALLOWED_TAGS: ['b', 'i', 'u', 'sub', 'sup', 'span'], ALLOWED_ATTR: ['style'] };
        const sanitizedContent = DOMPurify.sanitize(value, options);
        return this.sanitized.bypassSecurityTrustHtml(sanitizedContent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: SanitizedHtmlPipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: SanitizedHtmlPipe, name: "sanitizedHtml" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: SanitizedHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitizedHtml'
                }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }] });

class PreventDefault {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.unlisten = this.renderer.listen(this.el.nativeElement, this.eventName || 'click', (event) => {
            event.preventDefault();
        });
    }
    ngOnDestroy() {
        this.unlisten();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: PreventDefault, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.3", type: PreventDefault, selector: "[rcpPreventDefault]", inputs: { eventName: ["rcpPreventDefault", "eventName"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: PreventDefault, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rcpPreventDefault]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { eventName: [{
                type: Input,
                args: ['rcpPreventDefault']
            }] } });

class ReadCubeCommonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeCommonModule, declarations: [KeysPipe,
            BytesPipe,
            CapitalizePipe,
            ReplacePipe,
            ReversePipe,
            SanitizedUrlPipe,
            SortPipe,
            TrimPipe,
            TruncateURLPipe,
            HighlightPipe,
            SanitizedHtmlPipe,
            PreventDefault], exports: [KeysPipe,
            BytesPipe,
            CapitalizePipe,
            ReplacePipe,
            ReversePipe,
            SanitizedUrlPipe,
            SortPipe,
            TrimPipe,
            TruncateURLPipe,
            HighlightPipe,
            SanitizedHtmlPipe,
            PreventDefault] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeCommonModule, providers: [
            HighlightPipe
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        KeysPipe,
                        BytesPipe,
                        CapitalizePipe,
                        ReplacePipe,
                        ReversePipe,
                        SanitizedUrlPipe,
                        SortPipe,
                        TrimPipe,
                        TruncateURLPipe,
                        HighlightPipe,
                        SanitizedHtmlPipe,
                        PreventDefault
                    ],
                    exports: [
                        KeysPipe,
                        BytesPipe,
                        CapitalizePipe,
                        ReplacePipe,
                        ReversePipe,
                        SanitizedUrlPipe,
                        SortPipe,
                        TrimPipe,
                        TruncateURLPipe,
                        HighlightPipe,
                        SanitizedHtmlPipe,
                        PreventDefault
                    ],
                    providers: [
                        HighlightPipe
                    ]
                }]
        }] });

function chunkify(arr, size = 1) {
    const chunks = [];
    for (let i = 0, j = arr.length; i < j; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}

class XHRFileUploader {
    upload(file, uploadUrl, contentType = 'application/octet-stream') {
        this.xhr = new XMLHttpRequest();
        return new Observable((observer) => {
            this.xhr.withCredentials = true;
            this.xhr.open('PUT', uploadUrl, true);
            this.xhr.setRequestHeader('Accept', 'application/json');
            this.xhr.setRequestHeader('Content-Type', contentType);
            this.xhr.upload.addEventListener('progress', e => observer.next(e));
            this.xhr.upload.addEventListener('error', e => observer.error(e));
            this.xhr.addEventListener('readystatechange', function (e) {
                if (this.readyState === 4 && this.status === 200) {
                    observer.complete();
                }
            });
            this.xhr.send(file);
        });
    }
    abort() {
        if (this.xhr) {
            this.xhr.abort();
        }
    }
}
function hex(buffer) {
    const arr = [];
    const view = new DataView(buffer);
    for (let i = 0; i < view.byteLength; i += 4) {
        const value = view.getUint32(i);
        const stringValue = value.toString(16);
        const padding = '00000000';
        const paddedValue = (padding + stringValue).slice(-padding.length);
        arr.push(paddedValue);
    }
    return arr.join('');
}
function readTextFromFile(file, encoding) {
    return new Promise(resolve => {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            resolve(reader.result?.toString());
        });
        reader.readAsText(file, encoding);
    });
}
function getFileHash(file, algorithm) {
    if (!window.crypto || !window.crypto.subtle) {
        return Promise.resolve(null);
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('error', () => resolve(null));
        reader.addEventListener('load', function () {
            window.crypto.subtle.digest({ name: algorithm }, this.result).then((buf) => resolve(hex(buf)), (err) => reject(err));
        });
        reader.readAsArrayBuffer(file);
    });
}
function getFileExt(filename) {
    const i = filename.lastIndexOf('.');
    if (i === -1) {
        return null;
    }
    const ext = filename.substring(i + 1, filename.length).toLowerCase();
    if (!ext.match(/^[0-9a-z]+$/)) {
        return null;
    }
    return ext;
}

function strtrunc(str, len, useWordBoundary) {
    if (str.length <= len) {
        return str;
    }
    const s = str.slice(0, len - 1);
    return (useWordBoundary ? s.slice(0, s.lastIndexOf(' ')) : s) + '...';
}

/**
 * Generated bundle index. Do not edit.
 */

export { BytesPipe, CapitalizePipe, HighlightPipe, KeysPipe, KeysPipeObject, PreventDefault, ReadCubeCommonModule, ReplacePipe, ReversePipe, SanitizedHtmlPipe, SanitizedUrlPipe, SortPipe, TrimPipe, TruncateURLPipe, XHRFileUploader, chunkify, getFileExt, getFileHash, readTextFromFile, strtrunc };
